/**
 * Fund Matching Service
 *
 * Provides deduplication and matching logic for fund instruments
 * to ensure cross-family consistency in MFO organizations.
 */

import { supabase } from "@/integrations/supabase/client";

export interface FundIdentifiers {
  secCik?: string;
  lei?: string;
  secFileNumber?: string;
  ein?: string;
}

export interface FundMatchCandidate {
  instrumentId: string;
  instrumentName: string;
  matchType: 'cik_exact' | 'lei_exact' | 'name_exact' | 'alias_exact' | 'name_fuzzy' | 'alias_fuzzy';
  confidence: number;
  issuer?: string;
  vintageYear?: number;
}

export interface FundMatchResult {
  status: 'high_confidence' | 'medium_confidence' | 'low_confidence' | 'no_match';
  candidates: FundMatchCandidate[];
  bestMatch?: FundMatchCandidate;
  shouldAutoLink: boolean;
  requiresConfirmation: boolean;
}

export interface FundInput {
  name: string;
  issuer?: string;
  identifiers?: FundIdentifiers;
  vintageYear?: number;
}

// Confidence thresholds
const HIGH_CONFIDENCE_THRESHOLD = 0.9;
const MEDIUM_CONFIDENCE_THRESHOLD = 0.7;

/**
 * Normalize a fund name for comparison
 * Removes common suffixes and standardizes spacing
 */
export function normalizeFundName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s*(lp|llc|l\.p\.|l\.l\.c\.|inc\.|fund|partners|limited partnership|limited liability company)\.?\s*$/gi, '')
    .replace(/[^a-z0-9\s]/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Validate SEC CIK format (10-digit number, may have leading zeros)
 */
export function validateCIK(cik: string): boolean {
  const normalized = cik.replace(/\D/g, '').padStart(10, '0');
  return /^\d{10}$/.test(normalized);
}

/**
 * Normalize CIK to standard 10-digit format
 */
export function normalizeCIK(cik: string): string {
  return cik.replace(/\D/g, '').padStart(10, '0');
}

/**
 * Validate LEI format (20-character alphanumeric)
 */
export function validateLEI(lei: string): boolean {
  return /^[A-Z0-9]{20}$/.test(lei.toUpperCase());
}

/**
 * Normalize LEI to uppercase
 */
export function normalizeLEI(lei: string): string {
  return lei.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Find matching instruments for a given fund name and identifiers
 */
export async function findMatchingFunds(
  orgId: string,
  input: FundInput
): Promise<FundMatchResult> {
  const candidates: FundMatchCandidate[] = [];

  // Normalize identifiers
  const normalizedCik = input.identifiers?.secCik
    ? normalizeCIK(input.identifiers.secCik)
    : undefined;
  const normalizedLei = input.identifiers?.lei
    ? normalizeLEI(input.identifiers.lei)
    : undefined;

  // Step 1: Try exact identifier matches
  if (normalizedCik && validateCIK(normalizedCik)) {
    const { data: cikMatch } = await supabase
      .from('instruments')
      .select('id, name, issuer, vintage_year')
      .eq('org_id', orgId)
      .eq('sec_cik', normalizedCik)
      .single();

    if (cikMatch) {
      candidates.push({
        instrumentId: cikMatch.id,
        instrumentName: cikMatch.name,
        matchType: 'cik_exact',
        confidence: 1.0,
        issuer: cikMatch.issuer ?? undefined,
        vintageYear: cikMatch.vintage_year ?? undefined,
      });
    }
  }

  if (normalizedLei && validateLEI(normalizedLei)) {
    const { data: leiMatch } = await supabase
      .from('instruments')
      .select('id, name, issuer, vintage_year')
      .eq('org_id', orgId)
      .eq('lei', normalizedLei)
      .single();

    if (leiMatch && !candidates.find(c => c.instrumentId === leiMatch.id)) {
      candidates.push({
        instrumentId: leiMatch.id,
        instrumentName: leiMatch.name,
        matchType: 'lei_exact',
        confidence: 1.0,
        issuer: leiMatch.issuer ?? undefined,
        vintageYear: leiMatch.vintage_year ?? undefined,
      });
    }
  }

  // If we have a high-confidence identifier match, return early
  if (candidates.length > 0 && candidates[0].confidence >= HIGH_CONFIDENCE_THRESHOLD) {
    return {
      status: 'high_confidence',
      candidates,
      bestMatch: candidates[0],
      shouldAutoLink: true,
      requiresConfirmation: false,
    };
  }

  // Step 2: Try name-based matching using the database function
  const { data: nameMatches } = await supabase
    .rpc('find_matching_instruments', {
      p_org_id: orgId,
      p_name: input.name,
      p_cik: normalizedCik ?? null,
      p_lei: normalizedLei ?? null,
      p_threshold: MEDIUM_CONFIDENCE_THRESHOLD,
    });

  if (nameMatches && nameMatches.length > 0) {
    for (const match of nameMatches) {
      // Skip if we already have this instrument from identifier matching
      if (candidates.find(c => c.instrumentId === match.instrument_id)) {
        continue;
      }

      candidates.push({
        instrumentId: match.instrument_id,
        instrumentName: match.instrument_name,
        matchType: match.match_type as FundMatchCandidate['matchType'],
        confidence: match.confidence,
      });
    }
  }

  // Sort by confidence descending
  candidates.sort((a, b) => b.confidence - a.confidence);

  // Determine result status
  if (candidates.length === 0) {
    return {
      status: 'no_match',
      candidates: [],
      shouldAutoLink: false,
      requiresConfirmation: false,
    };
  }

  const bestMatch = candidates[0];

  if (bestMatch.confidence >= HIGH_CONFIDENCE_THRESHOLD) {
    return {
      status: 'high_confidence',
      candidates,
      bestMatch,
      shouldAutoLink: true,
      requiresConfirmation: false,
    };
  }

  if (bestMatch.confidence >= MEDIUM_CONFIDENCE_THRESHOLD) {
    return {
      status: 'medium_confidence',
      candidates,
      bestMatch,
      shouldAutoLink: false,
      requiresConfirmation: true,
    };
  }

  return {
    status: 'low_confidence',
    candidates,
    bestMatch,
    shouldAutoLink: false,
    requiresConfirmation: true,
  };
}

/**
 * Create a new instrument in the master catalog (org-level)
 */
export async function createMasterCatalogFund(
  orgId: string,
  input: FundInput & { type: string },
  userId?: string
): Promise<{ id: string } | null> {
  const { data, error } = await supabase
    .from('instruments')
    .insert({
      org_id: orgId,
      family_id: null, // Master catalog is org-level
      name: input.name,
      issuer: input.issuer,
      type: input.type as 'private_fund' | 'public_fund',
      vintage_year: input.vintageYear,
      sec_cik: input.identifiers?.secCik ? normalizeCIK(input.identifiers.secCik) : null,
      lei: input.identifiers?.lei ? normalizeLEI(input.identifiers.lei) : null,
      sec_file_number: input.identifiers?.secFileNumber,
      ein: input.identifiers?.ein,
      identifier_status: input.identifiers?.secCik || input.identifiers?.lei ? 'unverified' : 'unverified',
    })
    .select('id')
    .single();

  if (error) {
    console.error('Failed to create master catalog fund:', error);
    return null;
  }

  // Also add the original name as an alias
  if (data) {
    await addFundAlias(orgId, data.id, input.name, 'system', userId);
  }

  return data;
}

/**
 * Add a name alias for a fund
 */
export async function addFundAlias(
  orgId: string,
  instrumentId: string,
  alias: string,
  source: 'document_extraction' | 'manual' | 'sec_filing' | 'system' = 'manual',
  userId?: string
): Promise<boolean> {
  const { error } = await supabase
    .from('fund_name_aliases')
    .insert({
      org_id: orgId,
      instrument_id: instrumentId,
      alias,
      source,
      created_by: userId,
    });

  if (error) {
    // Ignore duplicate alias errors
    if (error.code !== '23505') {
      console.error('Failed to add fund alias:', error);
      return false;
    }
  }

  return true;
}

/**
 * Merge two duplicate funds into one
 * All positions, aliases, and references are moved to the target fund
 */
export async function mergeFunds(
  orgId: string,
  targetInstrumentId: string,
  sourceInstrumentId: string,
  userId?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get source instrument name for alias
    const { data: sourceInstrument } = await supabase
      .from('instruments')
      .select('name')
      .eq('id', sourceInstrumentId)
      .single();

    // Move all positions from source to target
    const { error: positionsError } = await supabase
      .from('positions')
      .update({ instrument_id: targetInstrumentId })
      .eq('instrument_id', sourceInstrumentId)
      .eq('org_id', orgId);

    if (positionsError) {
      throw new Error(`Failed to move positions: ${positionsError.message}`);
    }

    // Move all aliases from source to target
    const { error: aliasesError } = await supabase
      .from('fund_name_aliases')
      .update({ instrument_id: targetInstrumentId })
      .eq('instrument_id', sourceInstrumentId)
      .eq('org_id', orgId);

    if (aliasesError) {
      throw new Error(`Failed to move aliases: ${aliasesError.message}`);
    }

    // Add source name as an alias of target
    if (sourceInstrument?.name) {
      await addFundAlias(orgId, targetInstrumentId, sourceInstrument.name, 'system', userId);
    }

    // Delete the source instrument
    const { error: deleteError } = await supabase
      .from('instruments')
      .delete()
      .eq('id', sourceInstrumentId)
      .eq('org_id', orgId);

    if (deleteError) {
      throw new Error(`Failed to delete source instrument: ${deleteError.message}`);
    }

    return { success: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error('Failed to merge funds:', message);
    return { success: false, error: message };
  }
}

/**
 * Find potential duplicate funds in the organization
 */
export async function findPotentialDuplicates(
  orgId: string,
  threshold: number = 0.8
): Promise<Array<{
  fund1: { id: string; name: string };
  fund2: { id: string; name: string };
  similarity: number;
  sameIssuer: boolean;
}>> {
  // Get all private funds in the org
  const { data: funds } = await supabase
    .from('instruments')
    .select('id, name, issuer')
    .eq('org_id', orgId)
    .in('type', ['private_fund', 'public_fund']);

  if (!funds || funds.length < 2) {
    return [];
  }

  const duplicates: Array<{
    fund1: { id: string; name: string };
    fund2: { id: string; name: string };
    similarity: number;
    sameIssuer: boolean;
  }> = [];

  // Compare each pair of funds
  for (let i = 0; i < funds.length; i++) {
    for (let j = i + 1; j < funds.length; j++) {
      const fund1 = funds[i];
      const fund2 = funds[j];

      // Simple similarity calculation (could be enhanced)
      const similarity = calculateNameSimilarity(fund1.name, fund2.name);
      const sameIssuer = fund1.issuer && fund2.issuer
        ? normalizeFundName(fund1.issuer) === normalizeFundName(fund2.issuer)
        : false;

      if (similarity >= threshold || (similarity >= 0.6 && sameIssuer)) {
        duplicates.push({
          fund1: { id: fund1.id, name: fund1.name },
          fund2: { id: fund2.id, name: fund2.name },
          similarity,
          sameIssuer,
        });
      }
    }
  }

  // Sort by similarity descending
  return duplicates.sort((a, b) => b.similarity - a.similarity);
}

/**
 * Calculate similarity between two fund names (0-1)
 * Uses a simple approach; the database function uses pg_trgm for better accuracy
 */
export function calculateNameSimilarity(name1: string, name2: string): number {
  const n1 = normalizeFundName(name1);
  const n2 = normalizeFundName(name2);

  if (n1 === n2) return 1.0;

  // Simple Jaccard similarity on words
  const words1 = new Set(n1.split(' ').filter(w => w.length > 1));
  const words2 = new Set(n2.split(' ').filter(w => w.length > 1));

  const intersection = new Set([...words1].filter(x => words2.has(x)));
  const union = new Set([...words1, ...words2]);

  return intersection.size / union.size;
}
